import {Anchor, Group, GroupProps, TextProps} from "@mantine/core";
import {Link} from "react-router-dom";
import {useMemo} from "react";
import {Entity, EntityType, getEntityLinkConfig, isEntity} from "@ots-frontend/ots-data";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {EntityIcon, EntityIconProps} from "../../icon/EntityIcon/EntityIcon";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";

// moved to ots-data so the Tailwind library shares the same entity routes
export {getEntityLink, getEntityLinkConfig} from "@ots-frontend/ots-data";

export type EntityAnchorProps = GroupProps & {
  value: Entity | Partial<{ objectId: string, objectName: string, objectType: EntityType | string }>,
  icon?: Omit<EntityIconProps, 'entityType'> | boolean
  textProps?: TextProps
}

export const EntityAnchor = (props: EntityAnchorProps) => {
  const {value, icon = false, textProps, spacing = "xs", ...rest} = props

  const {hasAccess} = useAuth()

  const name: string | undefined = useMemo(() => {
    if (isEntity(value)) {
      return value.displayName
    } else {
      return value.objectName
    }
  }, [value])

  const entityType = useMemo(() => {
    let entityType: string | undefined = undefined
    if (isEntity(value)) {
      entityType = value.entityType
    } else {
      entityType = value.objectType
    }
    return entityType
  }, [value])

  const url: string | undefined = useMemo(() => {
    let id: string | undefined = undefined
    let urlPart: string | undefined = undefined

    if (isEntity(value)) {
      id = value.id
    } else {
      id = value.objectId
    }

    if (id && id.length > 0 && id !== "-1") {
      if (entityType) {
        const config = getEntityLinkConfig(entityType)
        if (config) {
          urlPart = config.link
        }
      }
    }

    return (id && urlPart) ? `/${urlPart}/profile?id=${id}` : undefined
  }, [value, entityType])

  const canView = useMemo(() => {
    if (entityType) {
      const config = getEntityLinkConfig(entityType)
      if (config) {
        return hasAccess(config.permissionPrefix + '_view')
      }
    }

    return true
  }, [entityType, hasAccess])

  if (url) {
    return (
      <Group {...rest} spacing={spacing}>
        {(entityType && icon && icon) &&
          <EntityIcon
            {...(typeof icon !== 'boolean' ? icon : {})}
            entityType={entityType}
          />
        }
        {canView ? (
          <Anchor component={Link} to={url} span>
            <DefaultText {...textProps} value={name}/>
          </Anchor>
        ) : (
          <DefaultText {...textProps} value={name}/>
        )}
      </Group>
    )
  } else {
    return (
      <Group {...rest} spacing={spacing}>
        {(entityType && icon && icon) &&
          <EntityIcon
            {...(typeof icon !== 'boolean' ? icon : {})}
            entityType={entityType}
          />
        }
        <DefaultText {...textProps} value={name}/>
      </Group>
    )
  }
}
